import "leaflet/dist/leaflet.css";
import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from "react-leaflet";

const ITALY_BOUNDS = [[36.6, 6.7], [47.1, 18.5]];
const ROMAGNA_BOUNDS = [[43.85, 11.2], [44.85, 12.8]];

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
    map.fitBounds(bounds, { padding: [2, 2] });
  }, [map, bounds]);
  return null;
}

// Coordinate [lat, lon] delle città italiane presenti nelle anagrafiche
const CITY_COORDS = {
  "ravenna": [44.4184, 12.2035],
  "roma": [41.9028, 12.4964],
  "bologna": [44.4949, 11.3426],
  "lugo": [44.4203, 11.911],
  "gela": [37.0658, 14.251],
  "milano": [45.4642, 9.19],
  "russi": [44.376, 12.029],
  "bagnacavallo": [44.415, 11.979],
  "alfonsine": [44.504, 12.047],
  "venezia": [45.4408, 12.3155],
  "dogana": [43.9869, 12.473],
  "pianoro": [44.387, 11.342],
  "napoli": [40.8518, 14.2681],
  "fiumicino": [41.7714, 12.2399],
  "forli": [44.2226, 12.0407],
  "castello di brianza": [45.757, 9.346],
  "cerlongo goito": [45.248, 10.668],
  "castelnuovo di porto": [42.128, 12.547],
  "cesenatico": [44.199, 12.396],
  "san giovanni bianco": [45.866, 9.662],
  "vicenza": [45.5455, 11.5354],
  "cagliari": [39.2238, 9.1217],
  "sarre": [45.718, 7.258],
  "castenaso": [44.506, 11.47],
  "cortina d ampezzo": [46.5405, 12.1357],
  "azzano decimo": [45.887, 12.718],
  "pordenone": [45.956, 12.66],
};

const norm = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['`]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

function coordsFor(city) {
  const n = norm(city);
  if (CITY_COORDS[n]) return CITY_COORDS[n];
  const first = n.split(" ")[0];
  if (CITY_COORDS[first]) return CITY_COORDS[first];
  return null;
}

export default function ItalyMap({ data = [] }) {
  const [zone, setZone] = useState("italia");
  const points = data
    .map((g) => ({ ...g, pos: coordsFor(g.city) }))
    .filter((p) => p.pos);
  const missing = data.filter((g) => !coordsFor(g.city) && norm(g.city) !== "non indicata");
  const maxCount = Math.max(1, ...points.map((p) => p.count));

  return (
    <div data-testid="italy-map">
      <div className="toolbar" style={{ marginBottom: 8, justifyContent: "center" }}>
        <div className="tabs" style={{ margin: 0 }}>
          <button className={zone === "italia" ? "active" : ""} onClick={() => setZone("italia")} data-testid="map-zoom-italia">Italia</button>
          <button className={zone === "romagna" ? "active" : ""} onClick={() => setZone("romagna")} data-testid="map-zoom-romagna">Romagna</button>
        </div>
      </div>
      <div style={{ height: 640, maxWidth: 480, margin: "0 auto", borderRadius: 14, overflow: "hidden", border: "1px solid rgba(255,255,255,.12)" }}>
        <MapContainer
          bounds={ITALY_BOUNDS}
          maxBounds={[[34.5, 4.5], [48.5, 20.5]]}
          maxBoundsViscosity={1.0}
          minZoom={5}
          zoomSnap={0.25}
          zoomDelta={0.5}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%", background: "#0a1228" }}
        >
          <FitBounds bounds={zone === "romagna" ? ROMAGNA_BOUNDS : ITALY_BOUNDS} />
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          {points.map((p) => {
            // scala logaritmica: le città con tanti clienti non coprono più mezza regione
            const r = 5 + 13 * (Math.log(1 + p.count) / Math.log(1 + maxCount));
            return (
              <CircleMarker
                key={p.city}
                center={p.pos}
                radius={r}
                pathOptions={{ color: "#22d3ee", fillColor: "#22d3ee", fillOpacity: 0.45, weight: 2 }}
              >
                <Tooltip direction="top" offset={[0, -2]} opacity={1} permanent={p.count === maxCount}>
                  <strong>{p.city}</strong>: {p.count} {p.count === 1 ? "cliente" : "clienti"}
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
      {missing.length > 0 && (
        <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          Senza posizione su mappa: {missing.map((m) => `${m.city} (${m.count})`).join(", ")}
        </p>
      )}
    </div>
  );
}
